import { Injectable, effect, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { RealtimeEvent } from '../realtime/realtime.types';
import { RealtimeService } from '../realtime/realtime.service';
import { SessionService } from '../other/session';
import { ToastService } from '../other/toast';
import { BrowserNotificationService } from './browser-notification.service';
import { NotificationDedupService } from './notification-dedup.service';

/**
 * SignalR → toast/browser-notification bridge.
 *
 * The notification badge ([[NotificationBadgeService]]) updates from SignalR
 * user-stream events but doesn't show a pop-up. This bridge does — it
 * subscribes to the same stream and fires the existing toast + browser
 * notification APIs when a `UserNotification` arrives.
 *
 * Reads the fat payload from Phase 4 (`{ title, body, htmlBody, type,
 * category, minuteId, metaData }`), so no extra HTTP round-trip.
 *
 * Lifecycle: instantiated once at app start (via the appInitializer in
 * `app.config.ts`). An internal effect watches `currentUser()` and
 * (re)subscribes to the user stream on login / enterprise switch.
 *
 * Parallel-run note: if both ntfy and SignalR are publishing, web users will
 * see double toasts for the brief period both channels run. The current rollout
 * plan tolerates this — once ntfy is removed (Phase 7c) or `useLegacyNtfy` is
 * flipped on, only one path emits.
 */
@Injectable({ providedIn: 'root' })
export class NotificationToastBridge {
  private realtime = inject(RealtimeService);
  private session = inject(SessionService);
  private toast = inject(ToastService);
  private browser = inject(BrowserNotificationService);
  private dedup = inject(NotificationDedupService);

  private subscription: Subscription | null = null;
  private lastUserId: string | null = null;

  constructor() {
    effect(() => {
      const userId = this.session.currentUser()?.userId ?? null;
      if (userId === this.lastUserId) return;
      this.lastUserId = userId;

      this.subscription?.unsubscribe();
      this.subscription = null;
      if (!userId) return;

      this.subscription = this.realtime
        .subscribeToUserStream()
        .subscribe((evt) => this.handle(evt));
    });
  }

  private handle(evt: RealtimeEvent): void {
    if (!evt || evt.entity !== 'UserNotification' || evt.kind !== 'Added') return;
    const payload = evt.payload as
      | {
          title?: string;
          body?: string;
          htmlBody?: string;
          type?: string;
          category?: string;
          minuteId?: number;
          metaData?: Record<string, string>;
        }
      | null;
    if (!payload) return;

    // Cross-channel dedup: suppress if the FCM foreground push for the same
    // UserNotification already presented (both keyed on the notificationId / entityId).
    if (!this.dedup.shouldPresent(evt.entityId)) return;

    const title = payload.title || 'Notification';
    const body = payload.body || '';

    // Browser notification — works when the tab is hidden but the app is
    // still running. Requires permission; falls through silently if denied.
    void this.browser.showNotification(title, {
      body,
      tag: String(evt.entityId),
      data: payload.metaData,
    });

    // In-page toast — matches the severity convention the legacy ntfy path used.
    this.toast.showToast('contrast', title, body);
  }
}
